import React, { useContext, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import ModalForm, { ImodalFormRef } from '@/components/ModalForm';
import { IFieldItem } from '@/components/CustomForm';
import BaseMobileInput from '@/components/BaseMobileInput';
import { parseAreaMobileStr } from '@/components/BaseMobileInput/helper';
import BaseEmailInput, { EMAIL_REG } from '@/components/BaseEmailInput';
import PhoneCheckModal, { PhoneCheckModalRef } from '@/components/PhoneCheckModal';
import IntlContext from '@/components/Intl/IntlContext';
import { TenantGlobalConfig } from '@/components/Intl/IntlContext/helper';

import { IguestInfo } from '@/redux/reducers/guest';
import {
  fetchCardInfoByWyuid,
  fetchCheckLeave,
  fetchCustomerInfo,
  fetchCustomerInfoByWyuid,
  fetchUpdateSingleCustomer,
} from '@/api/guest';
import { ContractType, MemberType } from '@/config';
import { renderIntlLabel } from '@/utils';

export interface GuestInfoModalRefProps {
  openModal?: (..._) => void;
}
interface GuestInfoModalProps {
  myRef?: any;
  onEditModalSuccess?: (..._) => void;
}

const GuestInfoModal: React.FC<GuestInfoModalRefProps & GuestInfoModalProps> = ({
  myRef,
  onEditModalSuccess,
}) => {
  const intlCtx = useContext(IntlContext);

  const [selectedItem, setSelectedItem] = useState<Partial<IguestInfo>>();
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  const guestModalRef = useRef<ImodalFormRef>();
  const phoneCheckModalRef = useRef<PhoneCheckModalRef>();

  const initBasePersonInfo = async (info) => {
    if (info?.wyuid) {
      try {
        Promise.all([
          fetchCardInfoByWyuid({ wyuid: info.wyuid }),
          fetchCheckLeave({ wyuid: info.wyuid }),
        ]).then(([res, checkRes]) => {
          const newInfo = res.data || {};
          newInfo.certType = newInfo.certificateType;
          // memberType 当前接口返回废弃，从入口处带过来
          newInfo.memberType = info?.memberType;
          const leaveFlag = !!checkRes?.data;
          setSelectedItem({ ...info, ...newInfo, leaveFlag });
        });
      } catch (error) {
        //
      }
    }
  };

  const handleBlur = async () => {
    const values = guestModalRef.current?.getFieldsValue();
    // 当前姓名、证件类型、证件号输入发生改变时获取网鱼uid
    const { name, certType, certNo } = values || {};
    if (name && certType && certNo) {
      const res = await fetchCustomerInfo({
        name,
        certType,
        certNo,
      });
      const { data } = res || {};
      const { wyuid, mobile, email, remark, certType: type } = data || {};
      setSelectedItem({
        ...selectedItem,
        wyuid,
        name,
        certNo,
        mobile,
        email,
        remark,
        certType: type,
      });
    }
  };

  // 更新会员
  const updateGuestInfo = async (params) => {
    try {
      const res = await fetchUpdateSingleCustomer(params);
      if (res?.code === 0) {
        guestModalRef.current.closeModal();
        if (typeof onEditModalSuccess === 'function') {
          onEditModalSuccess();
        }
      }
    } catch (error) {
      //
    }
  };

  const handleEditGuest = async (value) => {
    const { wyuid, recNo, leaveFlag } = selectedItem;
    const params = { ...value };
    params.name = params.name?.trim();
    if (wyuid) {
      // 可能存在手机号加密,拿明文手机号后端不支持加密提交
      if (params.mobile?.includes('*') || params.email?.includes('*')) {
        const plaintextRes = await fetchCustomerInfoByWyuid({ wyuid });
        if (plaintextRes.code === 0) {
          if (params.mobile?.includes('*')) {
            params.mobile = plaintextRes.data.mobile;
          }
          if (params.email?.includes('*')) {
            params.email = plaintextRes.data.email;
          }
        }
      }

      setUpdateLoading(true);
      phoneCheckModalRef.current
        .showPhoneCheck([
          { wyuid, name: params?.name, mobile: params?.mobile, email: params?.email },
        ])
        .then(async (checkRes) => {
          if (!checkRes.success) return;
          updateGuestInfo({
            wyuid,
            recNo,
            leaveFlag,
            ...params,
          }).finally(() => {
            setUpdateLoading(false);
          });
        })
        .finally(() => {
          setUpdateLoading(false);
        });
    } else {
      message.error(renderIntlLabel('guest_msg_wangyu_uid_empty'));
    }
  };

  const guestFormAttr: Array<IFieldItem> = useMemo(() => {
    const { leaveFlag, memberType } = selectedItem || {};
    return [
      {
        label: renderIntlLabel('order_roomorder_name'),
        key: 'name',
        rules: [
          {
            required: !leaveFlag,
            message: renderIntlLabel('order_roomorder_query_validator_please_input_charactor', {
              num: 40,
            }),
            max: 40,
          },
        ],
        editable: !leaveFlag,
        componentProps: {
          onBlur: handleBlur,
        },
      },
      {
        label: renderIntlLabel('order_roomorder_cert_type'),
        key: 'certType',
        type: 'select',
        rules: [{ message: renderIntlLabel('order_frontDesk_validate_select_cert_type') }],
        dataSource: TenantGlobalConfig.getSystemConfig().certTypesDataSource,
        editable: false,
        componentProps: {
          style: {
            width: 240,
          },
        },
      },
      {
        label: renderIntlLabel('order_roomorder_cert_no'),
        key: 'certNo',
        rules: [
          {
            message: renderIntlLabel('order_roomorder_query_validator_please_input_charactor', {
              num: 30,
            }),
            max: 30,
          },
        ],
        editable: false,
      },
      {
        label: renderIntlLabel('order_roomorder_mobile'),
        key: 'mobile',
        type: 'group',
        component: (
          <BaseMobileInput
            isKeyPerson={memberType === MemberType.HouseOwner}
            selectProps={{ disabled: leaveFlag, getPopupContainer: () => document.body }}
            inputProps={{ disabled: leaveFlag }}
          />
        ),
        rules: [
          {
            validator(_, val) {
              if (leaveFlag) {
                return Promise.resolve();
              }
              const { areaCode, mobileStr } = parseAreaMobileStr(val);
              // 只填一个
              if ((areaCode && !mobileStr) || (!areaCode && mobileStr)) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_mobile_error_format'),
                  ),
                );
              }
              // 是否修改过，修改过校验是否脱敏
              if (val && val !== selectedItem?.mobile && val.includes('*')) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_mobile_error_format'),
                  ),
                );
              }
              // 宾客修改之前有手机号就不能为空
              // 关键人常用联系方式手机号是否必填
              if (
                (selectedItem?.mobile ||
                  (memberType === MemberType.HouseOwner &&
                    intlCtx.TenantSysConfig.contractType === ContractType.MOBILE)) &&
                !val
              ) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_mobile_error_format'),
                  ),
                );
              }

              return Promise.resolve();
            },
          },
        ],
      },
      {
        label: renderIntlLabel('help_store_address_type_email'),
        key: 'email',
        type: 'group',
        component: (
          <BaseEmailInput isKeyPerson={memberType === MemberType.HouseOwner} disabled={leaveFlag} />
        ),
        rules: [
          {
            validator(_, val) {
              if (leaveFlag) {
                return Promise.resolve();
              }
              // 是否修改过，修改过校验邮箱格式是否正确
              if (val && val !== selectedItem?.email && !EMAIL_REG.test(val)) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_email_error_format'),
                  ),
                );
              }
              // 宾客修改之前有邮箱就不能为空
              if (
                (selectedItem?.email ||
                  (memberType === MemberType.HouseOwner &&
                    intlCtx.TenantSysConfig.contractType === ContractType.EMAIL)) &&
                !val
              ) {
                return Promise.reject(
                  new Error(
                    renderIntlLabel('order_frontDesk_validate_please_check_email_error_format'),
                  ),
                );
              }
              return Promise.resolve();
            },
          },
        ],
      },
      {
        label: renderIntlLabel('order_detail_guest_remark'),
        key: 'remark',
        type: 'textArea',
        placeholder: renderIntlLabel('order_frontDesk_placeholder_guest_remark_optional'),
        componentProps: {
          showCount: true,
          maxLength: 10000,
          style: {
            width: 300,
          },
        },
      },
    ];
  }, [selectedItem]);

  const handleOpenModal = ({ guestInfo }) => {
    guestModalRef.current.openModal();
    initBasePersonInfo(guestInfo);
  };

  useImperativeHandle(
    myRef,
    () => ({
      ...guestModalRef.current,
      openModal: handleOpenModal,
    }),
    [],
  );

  return (
    <>
      <ModalForm
        width={700}
        title={renderIntlLabel('guest_title_guest_info_edit')}
        ref={guestModalRef}
        formAttr={guestFormAttr}
        value={selectedItem}
        confirmLoading={updateLoading}
        onOk={handleEditGuest}
      />
      <PhoneCheckModal innerRef={phoneCheckModalRef} />
    </>
  );
};

export default React.forwardRef<GuestInfoModalRefProps, GuestInfoModalProps>((props, ref) => (
  <GuestInfoModal {...props} myRef={ref} />
));
